import { Slot, Slottable } from "@radix-ui/react-slot";
import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { focusRingStyles } from "../styles/focus";
import { Icon } from "./Icon";
import { IconLoader } from "./IconLoader/IconLoader";

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "prefix">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  prefix?: React.ReactElement;
  suffix?: React.ReactElement;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, prefix, suffix, variant, size, isLoading, svgOnly, align, asChild = false, children, ...rest },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";
    const { base, content, icon } = buttonVariants({
      variant,
      size,
      svgOnly,
      align,
      isLoading,
    });

    return (
      <ButtonContext.Provider value={{ size, svgOnly, variant }}>
        <Component className={base({ className })} ref={ref} {...rest} data-slot="button">
          {isLoading && prefix ? (
            <>
              <IconLoader animate className={icon()} />
            </>
          ) : (
            prefix
          )}
          {isLoading && !(prefix || suffix) && <IconLoader animate className={icon()} />}
          {asChild && children && React.isValidElement(children) ? (
            <Slottable>
              {React.cloneElement(
                children,
                children.props,
                <span data-slot="content" className={content()}>
                  {children.props.children}
                </span>,
              )}
            </Slottable>
          ) : children ? (
            <span data-slot="content" className={content()}>
              {children}
            </span>
          ) : null}
          {isLoading && suffix ? (
            <>
              <IconLoader animate className={icon()} />
            </>
          ) : (
            suffix
          )}
        </Component>
      </ButtonContext.Provider>
    );
  },
);

Button.displayName = "Button";

export const ButtonIcon = React.forwardRef<
  React.ElementRef<typeof Icon>,
  React.ComponentPropsWithoutRef<typeof Icon> & VariantProps<typeof buttonVariants>
>(({ className, size, ...props }, ref) => {
  const context = React.useContext(ButtonContext);
  const { icon } = buttonVariants({
    size: context.size ?? size,
  });

  return <Icon ref={ref} className={icon({ className })} {...props} />;
});
ButtonIcon.displayName = "ButtonIcon";

export const buttonVariants = tv({
  slots: {
    base: [
      focusRingStyles(),
      "cursor-pointer",

      // Base Variables
      "[--bttn-bg:var(--color-ds-teal-9)]",
      "[--bttn-bg-hover:#007272]",
      "[--bttn-bg-active:#183f3f]",
      "[--bttn-bg-disabled:var(--bttn-bg)]/50",
      "dark:[--bttn-bg-disabled:var(--bttn-bg)]/24",
      "[--bttn-text:var(--color-white)]",
      "[--bttn-text-hover:var(--bttn-text)]",
      "[--bttn-text-active:var(--bttn-text)]",
      "[--bttn-text-disabled:var(--bttn-text)]/80",
      "dark:[--bttn-text-disabled:var(--bttn-text)]/50",

      // Base Styles
      "text-(--bttn-text) bg-(--bttn-bg)",

      // State Styles
      "hover:bg-(--bttn-bg-hover) hover:text-(--bttn-text-hover)",
      "focus-visible:bg-(--bttn-bg-hover) focus-visible:text-(--bttn-text-hover)",
      "active:bg-(--bttn-bg-active) active:text-(--bttn-text-active)",
      "disabled:bg-(--bttn-bg-disabled) disabled:text-(--bttn-text-disabled)",

      "group/button no-underline inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors disabled:pointer-events-none",
    ],
    icon: "grid place-items-center text-current",
    content: "inline-block truncate px-1.5",
  },
  variants: {
    isLoading: {
      true: {
        base: "opacity-50 cursor-wait!",
      },
    },
    svgOnly: {
      true: {
        base: "grid place-items-center",
      },
    },
    align: {
      start: {
        content: "mr-auto",
      },
      end: {
        content: "ml-auto",
      },
    },
    variant: {
      default: {},
      secondary: {
        base: [
          "[--bttn-bg:var(--color-ds-neutral-12)]",
          "[--bttn-bg-hover:var(--color-ds-neutral-11)]",
          "[--bttn-bg-active:var(--color-ds-neutral-10)]",
          "[--bttn-bg-disabled:var(--bttn-bg)]/24",
          "dark:[--bttn-bg-disabled:#FBFCFF]/24",
          "[--bttn-text:var(--color-white)]",
          "dark:[--bttn-text:var(--color-black)]",
          "[--bttn-text-disabled:var(--bttn-text)]/80",
          "dark:[--bttn-text-disabled:#0A0D14]/50",
        ],
      },
      tertiary: {
        base: [
          "[--bttn-bg:var(--color-ds-neutral-2)]",
          "[--bttn-bg-hover:var(--color-ds-neutral-3)]",
          "[--bttn-bg-active:var(--color-ds-neutral-5)]",
          "[--bttn-bg-disabled:#FBFCFF]/24",
          "dark:[--bttn-bg-disabled:#1c2029]/24",
          "[--bttn-text:var(--color-black)]",
          "dark:[--bttn-text:var(--color-white)]",
          "[--bttn-text-disabled:var(--bttn-text)]/50",
          "dark:[--bttn-text-disabled:var(--bttn-text)]/80",
        ],
      },
      destructive: {
        base: [
          "[--bttn-bg:var(--color-ds-red-9)]",
          "[--bttn-bg-hover:var(--color-ds-red-10)]",
          "dark:[--bttn-bg-hover:#CE2B34]",
          "[--bttn-bg-active:var(--color-ds-red-12)]",
          "dark:[--bttn-bg-active:#64181A]",
          "[--bttn-bg-disabled:#dc3e42]/50",
          "dark:[--bttn-bg-disabled:#dc3e42]/24",
          "[--bttn-text:var(--color-white)]",
          "[--bttn-text-disabled:var(--bttn-text)]/80",
          "dark:[--bttn-text-disabled:var(--bttn-text)]/50",
        ],
      },

      warning: {
        base: [
          "[--bttn-bg:var(--color-ds-yellow-11)]",
          "[--bttn-bg-hover:var(--color-ds-yellow-11)]",
          "dark:[--bttn-bg-hover:#ffc100]",
          "[--bttn-bg-active:var(--color-ds-yellow-12)]",
          "dark:[--bttn-bg-active:#48381c]",
          "[--bttn-bg-disabled:#a06c00]/50",
          "dark:[--bttn-bg-disabled:#a06c00]/24",
          "[--bttn-text:var(--color-white)]",
          "[--bttn-text-hover:#fff9e9]",
          "dark:[--bttn-text-hover:var(--color-white)]",
          "[--bttn-text-active:var(--color-white)]",
          "[--bttn-text-disabled:var(--bttn-text)]/90",
          "dark:[--bttn-text-disabled:var(--bttn-text)]/70",
        ],
      },

      "ghost-destructive": {
        base: [
          "[--bttn-bg:transparent]",
          "[--bttn-bg-hover:var(--color-ds-red-3)]",
          "[--bttn-bg-active:var(--color-ds-red-4)]",
          "[--bttn-bg-disabled:transparent]",
          "[--bttn-text:var(--color-ds-red-11)]",
          "[--bttn-text-disabled:var(--bttn-text)]/70",
        ],
      },
      outline: {
        base: [
          "[--bttn-bg:var(--color-ds-surface-foundation)]",
          "[--bttn-bg-hover:var(--color-ds-neutral-2)]",
          "[--bttn-bg-active:var(--color-ds-neutral-3)]",
          "[--bttn-bg-disabled:var(--color-ds-surface-foundation)]",
          "[--bttn-text:var(--color-ds-text-primary)]",
          "[--bttn-text-disabled:var(--color-ds-text-disabled)]",
          "border border-ds-stroke-primary hover:border-ds-stroke-secondary",
        ],
      },
      ghost: {
        base: [
          "[--bttn-bg:transparent]",
          "[--bttn-bg-hover:var(--color-ds-neutral-2)]",
          "[--bttn-bg-active:var(--color-ds-neutral-3)]",
          "[--bttn-bg-disabled:transparent]",
          "[--bttn-text:var(--color-ds-text-primary)]",
          "[--bttn-text-disabled:var(--color-ds-text-disabled)]",
        ],
      },

      link: {
        base: [
          "[--bttn-bg:transparent]",
          "[--bttn-bg-hover:transparent]",
          "[--bttn-bg-active:transparent]",
          "[--bttn-bg-disabled:transparent]",
          "[--bttn-text:var(--color-ds-accent-primary-11)]",
          "[--bttn-text-hover:var(--color-ds-accent-primary-10)]",
          "[--bttn-text-active:var(--color-ds-accent-primary-12)]",
          "[--bttn-text-disabled:#007979]/80",
          "dark:[--bttn-text-disabled:#007979]/50",
          "underline-offset-4 hover:underline",
        ],
      },
    },
    size: {
      sm: {
        base: "h-8 px-1.5 text-xs",
        icon: "w-4 h-4",
      },
      default: {
        base: "h-9 px-2.5 py-2 text-sm",
        icon: "w-4 h-4",
      },
      lg: {
        base: "h-10 px-3.5 text-sm",
        icon: "w-4 h-4",
      },
      "sm-to-default": {
        base: "h-8 px-1.5 text-xs md:h-9 md:px-2.5 md:text-sm",
        icon: "w-4 h-4",
      },
      "sm-to-lg": {
        base: "h-8 px-1.5 text-xs md:h-10 md:px-3.5 md:text-sm",
        icon: "w-4 h-4",
      },
      icon: {
        base: "h-9 w-9", // Deprecate in favor of svgOnly
      },
    },
  },
  compoundVariants: [
    {
      svgOnly: true,
      size: "sm",
      className: {
        base: "h-8 w-8 p-0",
      },
    },
    {
      svgOnly: true,
      size: "default",
      className: {
        base: "h-9 w-9 p-0",
      },
    },
    {
      svgOnly: true,
      size: "lg",
      className: {
        base: "h-10 w-10 p-0",
      },
    },
  ],
  defaultVariants: {
    variant: "default",
    size: "default",
    svgOnly: false,
  },
});

export type ButtonVariants = VariantProps<typeof buttonVariants>;

const ButtonContext = React.createContext<ButtonVariants>({
  size: "default",
  svgOnly: false,
  variant: "default",
});
